import { action, computed, makeObservable, observable } from "mobx";
import type { IDicomUploadFetcher } from "./DicomUploadFetcher";
import type { DicomUploadCodeModel, SourceInfoModel } from "../../../../OpenApi/api";
import { mailRegex } from "../../../util/regex/regex";
import type { IUploadSourcesStore } from "./UploadSourcesStore";
import { IGoogleAnalyticsStore } from "../../google-analytics/GoogleAnalyticsStore";
import { GoogleAnalyticsEvents } from "../../../util/types/GoogleAnalyticsEvents";

export interface IUploadDialogStore {
	readonly ableToGenerateForSources: SourceInfoModel[];
	readonly selectedSourceToGenerateCodeFor: SourceInfoModel | null;
	readonly generatedUploadCode: DicomUploadCodeModel | null;
	readonly generatedUploadCodeLink: string;
	readonly generateError: boolean;
	readonly loading: boolean;
	readonly sendingMail: boolean;
	readonly sendingSms: boolean;
	readonly mailSent: boolean;
	readonly smsSent: boolean;
	readonly mailSentError: boolean;
	readonly smsSentError: boolean;
	readonly emailAddress: string;
	readonly emailAddressIsValid: boolean;
	readonly mobilePhone: string;
	readonly mobilePhoneIsValid: boolean;
	changeSelectedSource(sourceId: number): void;
	generateUploadCode(): Promise<void>;
	setEmailAddress(emailAddress: string): void;
	setMobilePhone(mobilePhone: string): void;
	sendMail(): Promise<void>;
	sendSms(): Promise<void>;
	reset(): void;
}

export class UploadDialogStore implements IUploadDialogStore {
	@observable
	public selectedSourceToGenerateCodeFor: SourceInfoModel | null = null;

	@observable
	public generatedUploadCode: DicomUploadCodeModel | null = null;

	@observable
	public generateError: boolean = false;

	@observable
	public loading: boolean = false;

	@observable
	public sendingMail: boolean = false;

	@observable
	public sendingSms: boolean = false;

	@observable
	public mailSent: boolean = false;

	@observable
	public smsSent: boolean = false;

	@observable
	public mailSentError: boolean = false;

	@observable
	public smsSentError: boolean = false;

	@observable
	public emailAddress: string = "";

	@observable
	public mobilePhone: string = "";

	constructor(
		private readonly uploadSourcesStore: IUploadSourcesStore,
		private readonly dicomUploadFetcher: IDicomUploadFetcher,
		private readonly googleAnalyticsStore: IGoogleAnalyticsStore,
		private readonly window: Window,
	) {
		makeObservable(this);
		const { ableToGenerateForSources } = uploadSourcesStore;
		if (ableToGenerateForSources.length === 1) {
			this.selectedSourceToGenerateCodeFor = ableToGenerateForSources[0];
		}
	}

	public get ableToGenerateForSources(): SourceInfoModel[] {
		const { uploadSourcesStore } = this;
		const { ableToGenerateForSources } = uploadSourcesStore;
		return ableToGenerateForSources;
	}

	@computed
	public get generatedUploadCodeLink(): string {
		const { generatedUploadCode, window } = this;

		let link = `${window.location.protocol}//${window.location.hostname}/POW4/upload/with-code/`;

		if (generatedUploadCode) {
			link += generatedUploadCode.sourceName + "?code=" + generatedUploadCode.code;
		}

		return link;
	}

	@computed
	public get emailAddressIsValid(): boolean {
		return this.emailAddress.length > 0 && mailRegex.test(this.emailAddress);
	}

	@computed
	public get mobilePhoneIsValid(): boolean {
		// Must start with + and have at least 10 digits
		return this.mobilePhone.length > 0 && /^\+\d{10,}$/.test(this.mobilePhone);
	}

	@action
	public changeSelectedSource(sourceId: number): void {
		const { ableToGenerateForSources } = this;
		const source = ableToGenerateForSources.find(s => s.id === sourceId);
		if (source) {
			this.selectedSourceToGenerateCodeFor = source;
		}
	}

	@action
	private setLoading(loading: boolean): void {
		this.loading = loading;
	}

	@action
	private setGenerateError(error: boolean): void {
		this.generateError = error;
	}

	@action
	private setGeneratedUploadCode(code: DicomUploadCodeModel | null): void {
		this.generatedUploadCode = code;
	}

	@action
	private setSendingMail(sending: boolean): void {
		this.sendingMail = sending;
	}

	@action
	private setMailSent(sent: boolean): void {
		this.mailSent = sent;
	}

	@action
	private setMailSentError(error: boolean): void {
		this.mailSentError = error;
	}

	@action
	private setSendingSms(sending: boolean): void {
		this.sendingSms = sending;
	}

	@action
	private setSmsSent(sent: boolean): void {
		this.smsSent = sent;
	}

	@action
	private setSmsSentError(error: boolean): void {
		this.smsSentError = error;
	}

	@action
	public async generateUploadCode(): Promise<void> {
		const { selectedSourceToGenerateCodeFor, dicomUploadFetcher } = this;
		if (!selectedSourceToGenerateCodeFor) {
			console.warn("No source selected for generating upload code");
			return;
		}

		this.reset();
		this.setLoading(true);
		this.setGenerateError(false);
		this.setGeneratedUploadCode(null);
		this.googleAnalyticsStore.sendGoogleAnalyticsEvent(GoogleAnalyticsEvents.DicomUploadCodeGenerate);

		try {
			const response = await dicomUploadFetcher.generateDicomUploadCode(selectedSourceToGenerateCodeFor.id);

			if (response?.dicomUploadCode) {
				this.setGeneratedUploadCode(response.dicomUploadCode);
				this.setGenerateError(false);
			} else {
				this.setGeneratedUploadCode(null);
				this.setGenerateError(true);
			}
		} catch (error) {
			console.error("Failed to generate upload code:", error);
			this.setGeneratedUploadCode(null);
			this.setGenerateError(true);
		} finally {
			this.setLoading(false);
		}
	}

	@action
	public setEmailAddress(value: string): void {
		this.emailAddress = value;
	}

	@action
	public setMobilePhone(value: string): void {
		this.mobilePhone = value;
	}

	@action
	public async sendMail(): Promise<void> {
		if (!this.generatedUploadCode || !this.emailAddressIsValid || this.mailSent) return;

		this.setSendingMail(true);
		this.setMailSentError(false);
		this.googleAnalyticsStore.sendGoogleAnalyticsEvent(GoogleAnalyticsEvents.DicomUploadCodeSendMail);

		try {
			const response = await this.dicomUploadFetcher.sendUploadCodeViaMailOrSMS(this.generatedUploadCode.id, this.emailAddress.trim(), "");

			this.setMailSent(response.sentMail);
			this.setMailSentError(false);
			// Auto-hide success state after 8 seconds
			setTimeout(() => this.setMailSent(false), 8000);
		} catch (error) {
			console.error("Error sending upload code via email:", error);
			this.setMailSentError(true);
		} finally {
			this.setSendingMail(false);
		}
	}

	@action
	public async sendSms(): Promise<void> {
		if (!this.generatedUploadCode || !this.mobilePhoneIsValid || this.smsSent) return;

		this.setSendingSms(true);
		this.setSmsSentError(false);
		this.googleAnalyticsStore.sendGoogleAnalyticsEvent(GoogleAnalyticsEvents.DicomUploadCodeSendSMS);

		try {
			const response = await this.dicomUploadFetcher.sendUploadCodeViaMailOrSMS(this.generatedUploadCode.id, "", this.mobilePhone.trim());

			if (response?.sentSms) {
				this.setSmsSent(response.sentSms);
				this.setSmsSentError(false);
				// Auto-hide success state after 8 seconds
				setTimeout(() => this.setSmsSent(false), 8000);
			} else {
				console.error("Failed to send upload code via SMS");
				this.setSmsSentError(true);
			}
		} catch (error) {
			console.error("Error sending upload code via SMS:", error);
			this.setSmsSentError(true);
		}

		this.setSendingSms(false);
	}

	@action
	public reset(): void {
		this.emailAddress = "";
		this.mobilePhone = "";
		this.generatedUploadCode = null;
		this.mailSent = false;
		this.smsSent = false;
		this.mailSentError = false;
		this.smsSentError = false;
		this.generateError = false;
		this.loading = false;
		this.sendingMail = false;
		this.sendingSms = false;
	}
}
